import { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { FormikProps, FormikProvider } from 'formik';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import NewFormCard, { FormCardContainer } from 'views/declarations/common/cards/NewFormCard';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import DeclarationInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import DeclarationDatepicker from 'components/ui/composed/declarations/formDatepicker/DeclarationDatepicker';
import useICS2Codelists from 'hooks/useICS2Codelists';
import { formatDateTimeToISO8601UTCTimestamp } from '../utils';
import { ArrivalPartyFields, ArrivalPartyReferences } from './cards/ArrivalPartiesCard';
import RelatedMrnsField from './cards/RelatedMrnsField';

const TRANSPORT = 'activeBorderTransportMeans';

const MARITIME = '1';
const AIR = '4';

type PartyTab = {
    tabName: string;
    path: string;
    identificationRequired?: boolean;
    references: ArrivalPartyReferences;
};

/**
 * Only the `Person Notifying the Arrival` reference numbers are given in the design; the notify
 * party follows the 13 13 series by analogy with the ENS form.
 *
 * The message allows up to nine notify parties. The form edits the first one, which is what the
 * design shows and what the flow needs; the payload stays a list either way.
 */
const parties: PartyTab[] = [
    {
        tabName: 'Person Notifying the Arrival',
        path: 'personNotifyingTheArrival',
        identificationRequired: true,
        references: {
            identificationNumber: '13 10 017 000',
            identifier: '13 10 029 015',
            type: '13 10 029 002',
        },
    },
    {
        tabName: 'Notify Party',
        path: 'notifyParties[0]',
        references: {
            identificationNumber: '13 13 017 000',
            identifier: '13 13 029 015',
            type: '13 13 029 002',
        },
    },
];

interface Props {
    formik: FormikProps<any>;
    viewOnly?: boolean;
}

const ICS2ArrivalNotificationForm = ({ formik, viewOnly }: Props) => {
    const { preloadICS2Codelists } = useICS2Codelists();
    const [activeTabKey, setActiveTabKey] = useState<string>(parties[0].tabName);

    useEffect(() => {
        preloadICS2Codelists([
            ICS2CodelistsMap.CL707,
            ICS2CodelistsMap.CL141,
            ICS2CodelistsMap.CL018,
            ICS2CodelistsMap.CL750,
            ICS2CodelistsMap.CL754,
        ]);
    }, [preloadICS2Codelists]);

    const mode = formik.values?.[TRANSPORT]?.modeOfTransport;

    const tabItems = useMemo(
        () =>
            parties.map((party) => ({
                key: party.tabName,
                label: party.tabName,
                children: (
                    <ArrivalPartyFields
                        path={party.path}
                        references={party.references}
                        identificationRequired={party.identificationRequired}
                        formik={formik}
                        viewOnly={viewOnly}
                    />
                ),
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [viewOnly, formik.values, formik.errors, formik.touched]
    );

    /**
     * The identity of the means of transport is recorded one way for a vessel and another for a
     * flight, and the message carries only the pair that matches the mode. Switching mode therefore
     * clears the fields belonging to the other one, so that a value entered before the switch is
     * not sent alongside the one that replaced it.
     */
    const onModeChange = (event: any) => {
        const next = event?.target?.value;
        formik.getFieldProps(`${TRANSPORT}.modeOfTransport`).onChange(event);

        if (next !== MARITIME) {
            formik.setFieldValue(`${TRANSPORT}.identificationNumber`, undefined);
            formik.setFieldValue(`${TRANSPORT}.typeOfIdentification`, undefined);
        }
        if (next !== AIR) {
            formik.setFieldValue(`${TRANSPORT}.conveyanceReferenceNumber`, undefined);
        }
    };

    const dateFieldProps = (field: string) => ({
        ...getFormikProps(`${TRANSPORT}.${field}`, formik),
        onChange: (value: any) =>
            formik.getFieldProps(`${TRANSPORT}.${field}`).onChange({
                target: {
                    value: formatDateTimeToISO8601UTCTimestamp(value),
                    name: `${TRANSPORT}.${field}`,
                },
            }),
    });

    return (
        <FormikProvider value={formik}>
            <NewFormCard title="Parties">
                <Tabs onChange={setActiveTabKey} activeKey={activeTabKey} items={tabItems} />
            </NewFormCard>

            <NewFormCard title="Customs Office Information">
                <FormCardContainer oneColumn>
                    <DeclarationSelect
                        hideCodelistMenu
                        required
                        condensed
                        disabled={viewOnly}
                        label="Actual Customs Office of First Entry Ref"
                        placeholder="Select Actual Customs Office Reference Number"
                        refNumber="17 08 001 000"
                        {...getFormikProps('actualCustomsOfficeOfFirstEntry.referenceNumber', formik)}
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL141,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />
                </FormCardContainer>
            </NewFormCard>

            <NewFormCard title="Transport Details">
                <FormCardContainer oneColumn>
                    <DeclarationSelect
                        hideCodelistMenu
                        required
                        condensed
                        disabled={viewOnly}
                        label="Mode of Transport at the Border"
                        placeholder="Select Mode of Transport"
                        refNumber="19 03 001 000"
                        {...getFormikProps(`${TRANSPORT}.modeOfTransport`, formik)}
                        fieldProps={{
                            ...formik.getFieldProps(`${TRANSPORT}.modeOfTransport`),
                            onChange: onModeChange,
                        }}
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL018,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />

                    {mode === MARITIME && (
                        <>
                            <DeclarationInput
                                required
                                condensed
                                disabled={viewOnly}
                                maxLength={35}
                                label="Active Border Transport Means ID"
                                placeholder="Vessel identification number"
                                refNumber="19 08 017 000"
                                {...getFormikProps(`${TRANSPORT}.identificationNumber`, formik)}
                            />
                            <DeclarationSelect
                                hideCodelistMenu
                                required
                                condensed
                                disabled={viewOnly}
                                label="Active Border Transport Means ID Type"
                                placeholder="Select Type of Identification"
                                refNumber="19 08 061 000"
                                {...getFormikProps(`${TRANSPORT}.typeOfIdentification`, formik)}
                                codeValidation={[]}
                                codelistProps={{
                                    codelistName: ICS2CodelistsMap.CL750,
                                    declarationType: DeclarationTypes.ICS2,
                                }}
                            />
                        </>
                    )}

                    {mode === AIR && (
                        <DeclarationInput
                            required
                            condensed
                            disabled={viewOnly}
                            maxLength={17}
                            label="Conveyance Reference Number"
                            placeholder="Flight number"
                            refNumber="19 05 001 000"
                            {...getFormikProps(`${TRANSPORT}.conveyanceReferenceNumber`, formik)}
                        />
                    )}

                    <DeclarationDatepicker
                        showTime
                        required
                        condensed
                        disabled={viewOnly}
                        showDateFormatLabel={false}
                        label="Actual date and time of arrival"
                        placeholder="Select Date and Time"
                        refNumber="15 01 001 000"
                        {...dateFieldProps('actualDateAndTimeOfArrival')}
                    />
                    <DeclarationDatepicker
                        showTime
                        condensed
                        disabled={viewOnly}
                        showDateFormatLabel={false}
                        label="Estimated date and time of arrival"
                        placeholder="Select Date and Time"
                        refNumber="15 03 001 000"
                        {...dateFieldProps('estimatedDateAndTimeOfArrival')}
                    />
                </FormCardContainer>
            </NewFormCard>

            <NewFormCard title="Related ENS Filings">
                <RelatedMrnsField formik={formik} path="relatedMrns" />
            </NewFormCard>
        </FormikProvider>
    );
};

export default ICS2ArrivalNotificationForm;
